import { Box, Chip, Container, Divider, MenuItem, Typography } from '@mui/material';
import {
	useGetAnswersByQuestionIdQuery,
	useGetQuestionDefaultAnswersQuery,
	useGetSurveyTemplateByIdQuery,
} from '../../../../../store/api/survey.api';
import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { usePaginatedData, useSortingParams } from '../../../../_core/hooks';
import { FieldOptionInterface, QuestionAnswerResponseDto, SurveyQuestionTypeEnum } from '@families-link/shared';
import { QuestionAnswersTable } from '../../../components/question-answers-table/question-answers-table.component';
import { Controller } from 'react-hook-form';
import CoreCalendar from '../../../../_core/components/_ui/core-date-picker/core-calendar/core-calendar.component';
import dayjs from 'dayjs';
import { useAnswerFilter } from '../../../../_core/hooks/use-answer-filter.hook';
import { AutocompleteTextField, CoreAutoComplete } from '../../../../_core/components/_ui/core-autocomplete';
import { CoreCheckbox } from '../../../../_core/components/_ui/core-checkbox';
import { CoreOverflowTypographyTooltip } from '../../../../_core/components/_ui/core-overflow-tooltip';
import { ChipCloseIcon } from '../../../../_core/components/icons';
import { chipDeleteIconStyles } from '../../../../_core/styles';
import { useGetDistrictListQuery } from '../../../../../store/api/district.api';
import { useDurationLocalSurveyFilter } from '../../../../_core/hooks/use-duration-local-survey.hook';
import { useDistrictSurveyLocalFilter } from '../../../../_core/hooks/use-district-filter-survey-local.hook';
import CoreCalendarAdvanced from '../../../../_core/components/_ui/core-date-picker/core-calendar/core-calendar-advanced.component';

export type QuestionAnswersPageProps = {
	isAdminRoute: boolean;
	currentDistrictId?: string | null;
};
export const QuestionAnswersPage: FC<QuestionAnswersPageProps> = ({ isAdminRoute, currentDistrictId }) => {
	const [t] = useTranslation();
	const isFirstRender = useRef(true);

	const { questionId, surveyTemplateId } = useParams();
	const { data: districtList } = useGetDistrictListQuery();
	const { data: surveyTemplate } = useGetSurveyTemplateByIdQuery(surveyTemplateId!);

	const { data: answerValuesList } = useGetQuestionDefaultAnswersQuery(
		{ questionId: questionId!, surveyTemplateId: surveyTemplateId! },
		{ skip: !questionId || !surveyTemplateId }
	);

	const {
		name: nameDuration,
		control: controlDuration,
		transformDuration,
		currentDuration,
	} = useDurationLocalSurveyFilter();
	const {
		register: registerDistrict,
		getValues: getValuesDistrict,
		control: controlDistrict,
		districtIds,
		districts,
	} = useDistrictSurveyLocalFilter();

	const {
		register: registerAnswerValue,
		getValues: getValuesAnswerValues,
		control: controlAnswer,
		answerValues,
	} = useAnswerFilter();
	const { sortingArray, sortingParams, handleSortingChange } = useSortingParams();

	const { requestObject, meta, handleResetPage, itemsList, fetchMore } = usePaginatedData<QuestionAnswerResponseDto>(
		useGetAnswersByQuestionIdQuery,
		{
			questionId,
			surveyTemplateId,
			...sortingParams,
			district: currentDistrictId,
			start: transformDuration.start
				? dayjs(transformDuration?.start).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
			end: transformDuration.start ? dayjs(transformDuration?.end).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ') : '',
			answersValue: answerValues?.join(','),
			districts: districtIds?.join(','),
		},
		{
			refetchOnMountOrArgChange: true,
		}
	);
	const { isFetching, isLoading } = requestObject;

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;

			return;
		}
		handleResetPage();
	}, [sortingArray, transformDuration, districts, answerValues, handleResetPage]);

	return (
		<Container
			maxWidth={false}
			sx={{ display: 'flex', flexDirection: 'column', height: '100%', paddingBottom: '16px', gap: 2 }}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				<Box display="flex" alignItems="center" gap={2}>
					<Typography sx={{ fontSize: '20px', fontWeight: 400 }}>{requestObject?.data?.question}</Typography>
					<Divider orientation="vertical" flexItem />
					<Typography sx={{ fontSize: '12px', fontWeight: 400 }}>{requestObject?.data?.questionType}</Typography>
				</Box>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						gap: 1,
						alignItems: 'center',
					}}
				>
					{isAdminRoute && (
						<Box sx={{ maxWidth: '270px', maxHeight: '48px' }}>
							<CoreAutoComplete
								multiple={true}
								{...registerDistrict('districts')}
								control={controlDistrict}
								controlSx={{ minWidth: '190px' }}
								options={districtList || []}
								isFilter
								getOptionLabel={(option) => option.name || ''}
								selectOnFocus={true}
								isOptionEqualToValue={(option, value) => option._id === value._id}
								renderInput={(params) => (
									<AutocompleteTextField
										{...params}
										placeholder={!getValuesDistrict('districts')?.length ? t('common.filter-district') : ''}
										sx={{
											'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
												padding: '4.5px 4px 5.5px 5px',
												minWidth: 0,
											},
											'& .MuiInputBase-input': {
												height: '100%',
												padding: '0',
											},
											'& .MuiOutlinedInput-root': {
												'&.Mui-focused fieldset': {
													borderColor: (theme) => theme.colors.blue,
												},
											},
										}}
									/>
								)}
								renderOption={(props, option) => (
									<MenuItem {...props} key={option._id}>
										<CoreOverflowTypographyTooltip>{option.name}</CoreOverflowTypographyTooltip>
									</MenuItem>
								)}
								renderTags={(value, getTagProps) => {
									const count = value.length - 1;

									return [
										value[0] && (
											<Chip
												sx={chipDeleteIconStyles}
												{...getTagProps({ index: 0 })}
												label={value[0]?.name}
												deleteIcon={<ChipCloseIcon />}
											/>
										),
										count > 0 && (
											<Chip
												sx={chipDeleteIconStyles}
												{...getTagProps({ index: count })}
												label={`+${count}`}
												deleteIcon={<ChipCloseIcon />}
											/>
										),
									];
								}}
							/>
						</Box>
					)}
					<Box sx={{ maxWidth: '270px', maxHeight: '48px' }}>
						{(requestObject?.data?.questionType === SurveyQuestionTypeEnum.MultiSelect ||
							requestObject?.data?.questionType === SurveyQuestionTypeEnum.Options) && (
							<CoreAutoComplete
								multiple={true}
								{...registerAnswerValue('answerValues')}
								control={controlAnswer}
								controlSx={{
									minWidth: '240px',
									maxWidth: '240px',
								}}
								isFilter
								options={answerValuesList?.map((s: FieldOptionInterface) => s.text) ?? []}
								getOptionLabel={(option) => {
									const answerValue = answerValuesList?.find((s) => s.text === option);

									return answerValue ? answerValue.text : '';
								}}
								selectOnFocus={true}
								isOptionEqualToValue={(option, value) => option === value}
								renderInput={(params) => (
									<AutocompleteTextField
										{...params}
										placeholder={!getValuesAnswerValues('answerValues')?.length ? t('common.filter-answer') : ''}
										sx={{
											'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
												padding: '4.5px 4px 5.5px 5px',
												minWidth: 0,
											},
										}}
									/>
								)}
								renderOption={(props, option, { selected }) => {
									const answerValue = answerValuesList?.find((s) => s.text === option);

									return (
										<MenuItem {...props} key={answerValue?.text}>
											<CoreCheckbox checked={selected} sx={{ mr: 2, p: 0, ml: 0 }} />
											<CoreOverflowTypographyTooltip>{answerValue?.text}</CoreOverflowTypographyTooltip>
										</MenuItem>
									);
								}}
								renderTags={(value, getTagProps) => {
									const count = value.length - 1;

									return [
										value[0] && (
											<Chip
												sx={chipDeleteIconStyles}
												{...getTagProps({ index: 0 })}
												label={answerValuesList?.find((s) => s.text === value[0])?.text}
												deleteIcon={<ChipCloseIcon />}
											/>
										),
										count > 0 && (
											<Chip
												sx={chipDeleteIconStyles}
												{...getTagProps({ index: count })}
												label={`+${count}`}
												deleteIcon={<ChipCloseIcon />}
											/>
										),
									];
								}}
							/>
						)}
					</Box>
					<Box sx={{ minWidth: '240px', maxWidth: '270px', maxHeight: '48px' }}>
						<Controller
							name={nameDuration}
							control={controlDuration}
							render={({ field: { value, ...fieldWithoutValue } }) => (
								<CoreCalendarAdvanced
									{...fieldWithoutValue}
									selectsRange
									inline={false}
									startDate={currentDuration?.length ? currentDuration[0] : null}
									endDate={currentDuration?.length ? currentDuration[1] : null}
									onChange={(dates: [Date | null, Date | null]) => {
										fieldWithoutValue.onChange(dates);
									}}
									onKeyDown={(e) => {
										e.preventDefault();
									}}
									onClear={() => {
										fieldWithoutValue.onChange([null, null]);
									}}
								/>
							)}
						/>
					</Box>
				</Box>
			</Box>

			<QuestionAnswersTable
				data={itemsList}
				loading={isFetching || isLoading}
				hasMore={meta?.hasNextPage}
				fetchMore={fetchMore}
				sorting={sortingArray}
				onSortingChange={handleSortingChange}
			/>
		</Container>
	);
};
